(() => {
  if (customElements.get('basket-size-inspiration')) return;

  class BasketSizeInspiration extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleDialogClose = this.handleDialogClose.bind(this);
      this.handleDocumentChange = this.handleDocumentChange.bind(this);
      this.handleProductInfoLoaded = this.handleProductInfoLoaded.bind(this);
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.lastImageTrigger = null;
      this.variantChangeUnsubscriber = null;
    }

    connectedCallback() {
      this.addEventListener('click', this.handleClick);
      this.dialog?.addEventListener('close', this.handleDialogClose);
      document.addEventListener('change', this.handleDocumentChange);
      document.addEventListener('product-info:loaded', this.handleProductInfoLoaded);
      document.addEventListener('shopify:section:load', this.handleSectionLoad);
      this.subscribeToVariantChanges();
      this.updateVisibility(this.readSelectedOptions());
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.dialog?.removeEventListener('close', this.handleDialogClose);
      document.removeEventListener('change', this.handleDocumentChange);
      document.removeEventListener('product-info:loaded', this.handleProductInfoLoaded);
      document.removeEventListener('shopify:section:load', this.handleSectionLoad);
      this.variantChangeUnsubscriber?.();
      this.variantChangeUnsubscriber = null;
    }

    get sectionId() {
      return String(this.dataset.sectionId || '');
    }

    get productInfo() {
      return this.closest('product-info');
    }

    get dialog() {
      return this.querySelector('[data-basket-dialog]');
    }

    handleClick(event) {
      const closeButton = event.target?.closest?.('[data-basket-dialog-close]');
      if (closeButton) {
        event.preventDefault();
        this.dialog?.close();
        return;
      }

      if (event.target === this.dialog) {
        this.dialog.close();
        return;
      }

      const imageTrigger = event.target?.closest?.('[data-basket-image-trigger]');
      if (imageTrigger) {
        if (this.openImage(imageTrigger)) event.preventDefault();
        return;
      }

      const addButton = event.target?.closest?.('[data-basket-add]');
      if (!addButton || addButton.classList.contains('is-loading')) return;
      event.preventDefault();
      this.addToCart(addButton);
    }

    openImage(trigger) {
      const dialog = this.dialog;
      if (!dialog || typeof dialog.showModal !== 'function') return false;

      const image = dialog.querySelector('[data-basket-dialog-image]');
      const title = dialog.querySelector('[data-basket-dialog-title]');
      const productLink = dialog.querySelector('[data-basket-dialog-product]');
      if (!image || !title || !productLink) return false;

      image.src = trigger.dataset.zoomSrc || trigger.href;
      image.alt = trigger.dataset.imageAlt || '';
      title.textContent = trigger.dataset.productTitle || '';
      productLink.href = trigger.dataset.productUrl || '#';
      this.lastImageTrigger = trigger;
      dialog.showModal();
      return true;
    }

    handleDialogClose() {
      this.lastImageTrigger?.focus();
      this.lastImageTrigger = null;
    }

    setStatus(message) {
      const status = this.querySelector('[data-basket-status]');
      if (status) status.textContent = message;
    }

    fetchConfig() {
      if (typeof fetchConfig === 'function') {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        return config;
      }

      return {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
      };
    }

    async addToCart(button) {
      button.classList.add('is-loading');
      button.classList.remove('is-added', 'has-error');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      this.setStatus(`מוסיף את ${button.dataset.productTitle || 'הצמח'} לעגלה`);

      const drawer = document.querySelector('cart-drawer');
      const cartPageItems = document.querySelector('cart-items');
      const onCartPage = Boolean(document.getElementById('main-cart-items') && cartPageItems);
      let pageTargets = null;
      let sections = [];

      if (onCartPage && typeof cartPageItems.getSectionsToRender === 'function') {
        pageTargets = cartPageItems.getSectionsToRender();
        sections = pageTargets.map((target) => target.section);
      } else if (drawer && typeof drawer.getSectionsToRender === 'function') {
        sections = drawer.getSectionsToRender().map((target) => target.id);
      }

      const config = this.fetchConfig();
      config.body = JSON.stringify({
        items: [{ id: Number(button.dataset.basketAdd), quantity: 1 }],
        sections,
        sections_url: window.location.pathname,
      });

      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

      try {
        const response = await fetch(addUrl, config);
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'add failed');
        }

        try {
          window.dataLayer?.push({
            event: 'basket_inspiration_add_to_cart',
            item_variant_id: button.dataset.basketAdd,
            item_name: button.dataset.productTitle || '',
          });
        } catch (_error) {
          // Analytics must never block the purchase action.
        }

        let cartUpdatePublished = false;
        if (
          typeof publish === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.cartUpdate
        ) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'basket-size-inspiration',
            productVariantId: button.dataset.basketAdd,
            cartData: data,
          });
          cartUpdatePublished = true;
        }

        button.classList.remove('is-loading');
        button.classList.add('is-added');
        button.disabled = false;
        button.removeAttribute('aria-busy');
        this.setStatus(`${button.dataset.productTitle || 'הצמח'} נוסף לעגלה`);

        if (onCartPage && pageTargets) {
          this.renderCartPage(pageTargets, data);
        } else if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(data);
        } else if (!cartUpdatePublished) {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }

        return data;
      } catch (_error) {
        button.classList.remove('is-loading', 'is-added');
        button.classList.add('has-error');
        button.disabled = false;
        button.removeAttribute('aria-busy');
        this.setStatus('לא הצלחנו להוסיף לעגלה. נסו שוב.');
        return null;
      }
    }

    renderCartPage(targets, data) {
      if (!data.sections) return;

      targets.forEach((target) => {
        const container = document.getElementById(target.id);
        const html = data.sections[target.section];
        if (!container || !html) return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector(target.selector);
        const destination = container.querySelector(target.selector) || container;
        if (source) destination.innerHTML = source.innerHTML;
      });

      document.querySelector('cart-items')?.classList.remove('is-empty');
      document.getElementById('main-cart-footer')?.classList.remove('is-empty');
    }

    subscribeToVariantChanges() {
      if (
        this.variantChangeUnsubscriber ||
        typeof subscribe !== 'function' ||
        typeof PUB_SUB_EVENTS === 'undefined' ||
        !PUB_SUB_EVENTS.variantChange
      ) {
        return;
      }

      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
    }

    handleVariantChange({ data } = {}) {
      if (!data?.sectionId || String(data.sectionId) !== this.sectionId) return;

      const values = data.variant?.options || data.variant?.title || [];
      this.updateVisibility(values);
    }

    handleDocumentChange(event) {
      const selector = event.target?.closest?.('variant-selects');
      if (!selector || !this.productInfo?.contains(selector)) return;

      requestAnimationFrame(() => this.updateVisibility(this.readSelectedOptions()));
    }

    handleProductInfoLoaded(event) {
      if (event.target !== this.productInfo) return;
      this.subscribeToVariantChanges();
      this.updateVisibility(this.readSelectedOptions());
    }

    handleSectionLoad(event) {
      if (event.target?.contains?.(this)) {
        this.updateVisibility(this.readSelectedOptions());
      }
    }

    readSelectedOptions() {
      const variantSelects = this.productInfo?.querySelector('variant-selects');
      const values = [];

      variantSelects?.querySelectorAll('input[type="radio"]:checked, select').forEach((control) => {
        if (control.tagName === 'SELECT') {
          Array.from(control.selectedOptions || []).forEach((option) => values.push(option.value));
        } else {
          values.push(control.value);
        }
      });

      if (values.length) return values;

      try {
        return JSON.parse(this.dataset.currentOptions || '[]');
      } catch (_error) {
        return [];
      }
    }

    isSize18(values) {
      const normalized = (Array.isArray(values) ? values : [values])
        .filter(Boolean)
        .join(' ')
        .normalize('NFKC')
        .replace(/[״”″]/g, '"')
        .replace(/\s+/g, ' ');

      return /(^|\D)18\s*ס\s*["']?\s*מ(?:\D|$)/i.test(normalized);
    }

    updateVisibility(values) {
      const shouldShow = this.isSize18(values);
      this.hidden = !shouldShow;
      this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    }
  }

  customElements.define('basket-size-inspiration', BasketSizeInspiration);
})();
